import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, AlertTriangle, CheckCircle2, Clock, DollarSign } from "lucide-react";

export interface ProviderHealthData {
  provider_name: string;
  status: "ok" | "slow" | "rate_limited" | "budget_exceeded" | "down" | "unknown";
  p50_ms?: number;
  p95_ms?: number;
  cost_7d_usd?: number;
  last_error_at?: string | null;
  last_error_code?: number | null;
  cooldown_until?: string | null;
  call_count_24h?: number;
  success_rate_24h?: number;
}

const STATUS_VARIANT: Record<ProviderHealthData["status"], { color: string; label: string; icon: typeof CheckCircle2 }> = {
  ok: { color: "border-primary/30 bg-primary/5 text-primary", label: "Operacional", icon: CheckCircle2 },
  slow: { color: "border-warning/30 bg-warning/5 text-warning-foreground", label: "Lento", icon: Clock },
  rate_limited: { color: "border-warning/30 bg-warning/5 text-warning-foreground", label: "Rate-limited", icon: AlertTriangle },
  budget_exceeded: { color: "border-destructive/30 bg-destructive/5 text-destructive", label: "Orçamento estourado", icon: DollarSign },
  down: { color: "border-destructive/30 bg-destructive/5 text-destructive", label: "Indisponível", icon: AlertTriangle },
  unknown: { color: "border-border bg-muted text-muted-foreground", label: "Desconhecido", icon: Activity },
};

export function ProviderHealthCard({ data }: { data: ProviderHealthData }) {
  const variant = STATUS_VARIANT[data.status] ?? STATUS_VARIANT.unknown;
  const Icon = variant.icon;
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base capitalize">{data.provider_name}</CardTitle>
          <Badge variant="outline" className={variant.color}>
            <Icon className="w-3 h-3 mr-1" />
            {variant.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Latência p50 / p95</span>
          <span className="font-mono">
            {data.p50_ms != null ? `${data.p50_ms}ms` : "—"}
            {" / "}
            {data.p95_ms != null ? `${data.p95_ms}ms` : "—"}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Custo 7d</span>
          <span className="font-mono">{data.cost_7d_usd != null ? `$${data.cost_7d_usd.toFixed(4)}` : "—"}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Sucesso 24h</span>
          <span className="font-mono">
            {data.success_rate_24h != null ? `${(data.success_rate_24h * 100).toFixed(1)}%` : "—"}
            {data.call_count_24h != null && ` (${data.call_count_24h})`}
          </span>
        </div>
        {data.last_error_at && (
          <div className="text-xs text-muted-foreground pt-1 border-t">
            Último erro: {new Date(data.last_error_at).toLocaleString("pt-BR")}
            {data.last_error_code && ` (HTTP ${data.last_error_code})`}
          </div>
        )}
        {data.cooldown_until && new Date(data.cooldown_until) > new Date() && (
          <div className="text-xs text-orange-600 dark:text-orange-400">
            Em cooldown até {new Date(data.cooldown_until).toLocaleTimeString("pt-BR")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
